import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import '../productPage/producthome.css';
import { Box, Typography } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadCrumbs from '../common/BreadCrumbs';
import LocationSelect from './LocationSelect';
import LocationCard from './LocationCard';
import ContactForm from './ContactForm';
import DistributorLocationCard from './DistributorsCard';
import { useGeographicRegions } from '../../graphql/pimcore/modules/geographic/contexts/GeographicRegionsProvider';
import { useDistributors } from '../../graphql/pimcore/modules/contacts/contexts/DistributorsProvider';
import { Distributor } from '../../graphql/pimcore/modules/contacts/types/Distributor';
import { PIMCORE_CONTENT_BASE_URL } from '../../utils/Pimcore';
import { Country } from '../../graphql/pimcore/modules/geographic/types/Country';
import { useOffices } from '../../graphql/pimcore/modules/contacts/contexts/OfficesProvider';
import { Office } from '../../graphql/pimcore/modules/contacts/types/Office';
import { useRepresentatives } from '../../graphql/pimcore/modules/contacts/contexts/RepresentativeProvider';
import { Representative } from '../../graphql/pimcore/modules/contacts/types/Representative';
import { SubnationalRegion } from '../../graphql/pimcore/modules/geographic/types/SubnationalRegion';
import RepresentativeSelect from './RepresentativeSelect';
import TabsComponent, { GetResponsiveWidth } from '../common/TabsComponent';
import { getTabTypography } from '../investorPage/investorRelationHome';
import { useResponsive } from '../../contexts/ResponsiveContext';
import {
    filterCountriesByRepresentatives,
    filterGeographicRegionsByDistributors,
    filterGeographicRegionsByOffices,
} from '../../graphql/pimcore/modules/geographic/tools/filters';
import { SalesAndResourcesContactPointsContent } from '../../graphql/pimcore/modules/siteContent/types/SalesAndResourcesContactPointsContent';
import OutterWrapper from '@/components/common/OutterWrapper';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

interface TabDefinition {
    /** Index of the tab in the tab list */
    index: number;
    route: string;
    /** Navigation path for breadcrumb */
    breadCrumbPath: string;
    label: string;
}

interface ContactUsHomeProps {
    contentTree: SalesAndResourcesContactPointsContent;
}

const ContactUsHome: FunctionComponent<ContactUsHomeProps> = ({ contentTree }) => {
    const [openMenu, setOpenMenu] = React.useState(false);
    const { isScreenXs } = useResponsive();
    const location = useLocation();
    const navigate = useNavigate();
    const geographicRegions = useGeographicRegions();
    const { countryById, subnationalRegionById } = useGeographicRegions();
    const {
        distributors,
        filteredDistributors,
        setSelectedCountryId,
        setSelectedGeographicRegionId,
    } = useDistributors();

    const {
        offices,
        filteredOffices,
        setSelectedOfficeCountryId,
        setSelectedOfficeGeographicRegionId,
    } = useOffices();

    const { representatives, filteredRepresentatives, setSelectedRepresentativeCountryId } =
        useRepresentatives();

    const tabDefinitions = useMemo<TabDefinition[]>(() => {
        const defs = [
            {
                index: 0,
                route: '/SalesResources/SalesOffices',
                breadCrumbPath: '/Home/SalesAndResources/SalesOffices',
                label: contentTree.SalesOffices.title.content,
            },
            {
                index: 1,
                route: '/SalesResources/Representatives',
                breadCrumbPath: '/Home/SalesAndResources/Representatives',
                label: contentTree.Representatives.title.content,
            },
            {
                index: 2,
                route: '/SalesResources/Distributors',
                breadCrumbPath: '/Home/SalesAndResources/Distributors',
                label: contentTree.Distributors.title.content,
            },
            {
                index: 3,
                route: '/SalesResources/ContactUs',
                breadCrumbPath: '/Home/SalesAndResources/ContactUs',
                label: contentTree.ContactUs.title.content,
            },
        ];
        // we assert that at least one tab definition exists (using [0]! to avoid undefined)
        if (defs.length === 0) {
            throw new Error('No tab definitions found');
        }
        return defs;
    }, [contentTree]);

    /** Get a tab definition by a matcher function; or the first tab definition if none matches */
    const getTabDefinition = useCallback(
        (matcher: (tab: TabDefinition) => boolean) => {
            const found = tabDefinitions.find(matcher);
            if (found) return found;

            if (!tabDefinitions[0]) {
                throw new Error('tabDefinitions must not be empty');
            }

            return tabDefinitions[0];
        },
        [tabDefinitions]
    );

    /** Get the current tab definition based on the current location */
    const getCurrentTabDefinition = useCallback(() => {
        return getTabDefinition((tab) => tab.route === location.pathname);
    }, [location.pathname, getTabDefinition]);

    const [currentTab, setCurrentTab] = useState<TabDefinition>(() => getCurrentTabDefinition());

    useEffect(() => {
        setCurrentTab(getCurrentTabDefinition());
    }, [getCurrentTabDefinition]);

    const transformDistributorsToLocationCard = useCallback(
        (
            distributors: Distributor[],
            countryById: Record<string, Country>
        ): { global: LocationCard[]; regional: LocationCard[] } => {
            const global: LocationCard[] = [];
            const regional: LocationCard[] = [];

            distributors
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                .forEach((distributor) => {
                    if (distributor.countryIds.length === 0 && !distributor.isGlobal) {
                        // Skip non-global distributors without countries; they should not appear
                        // in lists; they are used e.g. as Octopart logo providers
                        return;
                    }
                    if (distributor.freeFormText) {
                        console.log(
                            `[Distributor freeform] ${distributor.name} | isGlobal=${distributor.isGlobal} | freeFormText="${distributor.freeFormText}"`
                        );
                    }
                    const countryNames = distributor.countryIds.map(
                        (id) => countryById[id]?.name || id
                    );
                    const locationCard: LocationCard = {
                        title: distributor.name,
                        logo: distributor.logo
                            ? PIMCORE_CONTENT_BASE_URL + distributor.logo.fullpath
                            : undefined,
                        country: distributor.isGlobal ? '' : countryNames,
                        url: distributor.url ? distributor.url : '',
                        region: geographicRegions.geographicRegions
                            .filter((region) =>
                                region.countries.some((country) =>
                                    countryNames.includes(country.name)
                                )
                            )
                            .map((region) => region.name)
                            .join(', '),
                        freeformText1: distributor.freeFormText ?? '',
                    };

                    if (distributor.isGlobal && distributor.isFeatured) {
                        global.push(locationCard);
                    } else {
                        regional.push(locationCard);
                    }
                });
            return { global, regional };
        },
        [geographicRegions.geographicRegions]
    );

    const { regional: regionalDistributors } = useMemo(
        () => transformDistributorsToLocationCard(filteredDistributors, countryById),
        [filteredDistributors, countryById, transformDistributorsToLocationCard]
    );

    const { global: globalDistributors } = useMemo(
        () => transformDistributorsToLocationCard(distributors, countryById),
        [distributors, countryById, transformDistributorsToLocationCard]
    );

    const transformOfficesToLocationCard = (
        offices: Office[],
        countryById: Record<string, Country>
    ): LocationCard[] => {
        return offices.map((office) => {
            const countryName = countryById[office.countryId]?.name || office.countryId;
            return {
                title: office.name,
                freeformText1: '',
                freeformText2: '',
                addressLine1: office.salesAddressLine1 ?? '',
                addressLine2: office.salesAddressLine2 ?? '',
                addressLine3: office.salesAddressLine3 ?? '',
                country: countryName,
                url: '',
                phone: office.phone ?? '',
                email: office.email ?? '',
                logo: office.logo ? PIMCORE_CONTENT_BASE_URL + office.logo.fullpath : undefined,
            };
        });
    };

    const officeLocations = useMemo(
        () => transformOfficesToLocationCard(filteredOffices, countryById),
        [filteredOffices, countryById]
    );

    const transformRepresentativesToLocationCard = (
        representatives: Representative[],
        countryById: Record<string, Country>,
        subnationalRegionById: Record<string, SubnationalRegion>
    ): LocationCard[] => {
        return representatives.map((representative) => {
            const countryNames = representative.countryOrSubnational.map((id) => {
                return countryById[id]?.name || subnationalRegionById[id]?.name || id;
            });
            return {
                title: representative.name,
                freeformText1: representative.freeFormText ?? '',
                addressLine1: representative.salesAddressLine1 ?? '',
                addressLine2: representative.salesAddressLine2 ?? '',
                addressLine3: representative.salesAddressLine3 ?? '',
                country: countryNames.join(', '),
                phone: representative.phone ?? '',
                email: representative.email ?? '',
                logo: representative.logo
                    ? PIMCORE_CONTENT_BASE_URL + representative.logo.fullpath
                    : undefined,
            };
        });
    };

    const representativeLocations = useMemo(
        () =>
            transformRepresentativesToLocationCard(
                filteredRepresentatives,
                countryById,
                subnationalRegionById
            ),
        [filteredRepresentatives, countryById, subnationalRegionById]
    );

    const handleChange = useCallback(
        (_event: React.SyntheticEvent | null, newTabIndex: number) => {
            const newTab = getTabDefinition((tab) => tab.index === newTabIndex);
            setCurrentTab(newTab);
            navigate(newTab.route);
        },
        [navigate, getTabDefinition]
    );

    const renderTabPanel = (
        index: number,
        locations: LocationCard[],
        contentTree: SalesAndResourcesContactPointsContent,
        isDistributorLocation?: boolean,
        isOfficeLocation?: boolean
    ) => (
        <CustomTabPanel value={currentTab.index} index={index}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0',
                    width: '100%',
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    overflowY: 'hidden',
                    backgroundColor: 'white',
                }}
            >
                <Box sx={{ marginTop: '2rem', width: '100%', backgroundColor: '#F8F8F8' }}>
                    <Box
                        className="location-box"
                        sx={{
                            width: GetResponsiveWidth,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            margin: 'auto',
                            flex: '1 1 calc(100%)',
                        }}
                    >
                        {isOfficeLocation ? (
                            <LocationSelect
                                setSelectedCountryId={setSelectedOfficeCountryId}
                                setSelectedGeographicRegionId={setSelectedOfficeGeographicRegionId}
                                geographicRegions={filterGeographicRegionsByOffices(
                                    offices,
                                    geographicRegions.geographicRegions
                                )}
                                contentTree={contentTree}
                            />
                        ) : (
                            <RepresentativeSelect
                                setSelectedCountryId={setSelectedRepresentativeCountryId}
                                setSelectedSubnationalRegionId={setSelectedRepresentativeCountryId}
                                countries={filterCountriesByRepresentatives(
                                    representatives,
                                    geographicRegions.geographicRegions
                                )}
                                contentTree={contentTree}
                            />
                        )}
                    </Box>
                </Box>
                <Box sx={{ marginTop: '2rem', width: '100%', backgroundColor: '#FFFFFF' }}>
                    <Box
                        sx={{
                            width: GetResponsiveWidth,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            margin: 'auto',
                        }}
                    >
                        {isDistributorLocation ? (
                            <DistributorLocationCard
                                distributorLocationCards={locations}
                                isRegional={false}
                            />
                        ) : (
                            <LocationCard locationCards={locations} />
                        )}
                    </Box>
                </Box>
            </Box>
        </CustomTabPanel>
    );

    return (
        <OutterWrapper
            componentName={'SalesResources'}
            pathname={location.pathname}
            pageTitle={contentTree.pageTitle.content}
            pagesubTitle={contentTree.pageSubtitle.content}
            bannerImageUrl={contentTree.pageTitleBackgroundImage.imageUrl}
            breadcrumbsComponent={<BreadCrumbs navigationPath={currentTab.breadCrumbPath} />}
        >
            <Box sx={{ width: '100%', backgroundColor: 'white' }} data-testid={'outter_box'}>
                <TabsComponent
                    isMenuExpandMoreIcon
                    url={currentTab.index}
                    setOpenMenu={setOpenMenu}
                    openMenu={openMenu}
                    handleChange={handleChange}
                    menuItems={tabDefinitions.map((tab) => ({
                        label: tab.label,
                        value: tab.index,
                    }))}
                    tabTypography={{
                        ...getTabTypography,
                    }}
                />
                {contentTree && renderTabPanel(0, officeLocations, contentTree, false, true)}
                {contentTree && renderTabPanel(1, representativeLocations, contentTree)}
                <CustomTabPanel value={currentTab.index} index={2}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '0',
                            width: '100%',
                            boxSizing: 'border-box',
                            flexDirection: 'column',
                            overflowY: 'hidden',
                            backgroundColor: 'white',
                        }}
                    >
                        <Box
                            sx={{
                                marginTop: '2rem',
                                width: '100%',
                                backgroundColor: '#F8F8F8',
                            }}
                        >
                            <Box
                                sx={{
                                    width: GetResponsiveWidth,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    margin: 'auto',
                                }}
                            >
                                <LocationSelect
                                    setSelectedCountryId={setSelectedCountryId}
                                    setSelectedGeographicRegionId={setSelectedGeographicRegionId}
                                    geographicRegions={filterGeographicRegionsByDistributors(
                                        distributors,
                                        geographicRegions.geographicRegions
                                    )}
                                    contentTree={contentTree}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                marginTop: '2rem',
                                width: '100%',
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            <Box
                                sx={{
                                    width: GetResponsiveWidth,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'start',
                                    justifyContent: 'space-between',
                                    margin: 'auto',
                                }}
                            >
                                <Typography
                                    sx={{
                                        marginLeft: '1rem',
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '18px',
                                    }}
                                >
                                    {contentTree.Distributors.globalDistributorsLabel.content}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: GetResponsiveWidth,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    margin: 'auto',
                                }}
                            >
                                <DistributorLocationCard
                                    distributorLocationCards={globalDistributors}
                                    isRegional={false}
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: GetResponsiveWidth,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'start',
                                    justifyContent: 'space-between',
                                    margin: 'auto',
                                }}
                            >
                                <Typography
                                    sx={{
                                        marginLeft: '1rem',
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '18px',
                                        marginBottom: '0.6rem',
                                    }}
                                >
                                    {contentTree.Distributors.regionalDistributorsLabel.content}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: GetResponsiveWidth,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    margin: 'auto',
                                }}
                            >
                                <DistributorLocationCard
                                    distributorLocationCards={regionalDistributors}
                                    isRegional
                                />
                            </Box>
                        </Box>
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={currentTab.index} index={3}>
                    <Box
                        sx={{
                            width: GetResponsiveWidth,
                            margin: 'auto',
                            display: 'flex',
                            backgroundColor: '#FFFFFF',
                        }}
                    >
                        <ContactForm />
                    </Box>
                </CustomTabPanel>
            </Box>
        </OutterWrapper>
    );
};

export default ContactUsHome;
